import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import useVisibilityAwareInterval from '../hooks/useVisibilityAwareInterval';
import { Plus, X, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';
import SaveModeModal from '../components/SaveModeModal';

export default function Portfolio() {
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState(null);  // Phase 36.5.C — canonical
  const [positions, setPositions] = useState([]);
  const [health, setHealth] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ symbol: '', qty: '', entry: '', stop: '', target: '', note: '' });
  const [last, setLast] = useState(null);
  // Auto-Exit — when ON we watch each open position on every health
  // refresh and auto-close it (save_mode='learning') the instant its
  // last price crosses the stop OR target level. Persist across
  // reloads via localStorage so the operator's choice sticks.
  const [autoExit, setAutoExit] = useState(() => {
    try { return localStorage.getItem('atde:auto_exit') === '1'; } catch { return false; }
  });
  const toggleAutoExit = () => {
    setAutoExit((v) => {
      const next = !v;
      try { localStorage.setItem('atde:auto_exit', next ? '1' : '0'); } catch { /* noop */ }
      toast(next ? 'Auto-Exit ON — stop/target hits close to learning' : 'Auto-Exit OFF');
      return next;
    });
  };
  // Phase 51.M+ — save-mode modal state. Holds { open, position, mode }
  // where `mode` is "close" (needs exit price input) or "archive" (uses
  // the last live quote / entry fallback server-side).
  const [modal, setModal] = useState({ open: false, position: null, mode: 'close' });
  // Iter 84 — inline edit state for open positions. { id, qty, entry,
  // stop, target } while operator is editing; null when idle. Only one
  // position can be edited at a time.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Iter 85 — expanded history rows { [positionId]: true }
  const [historyOpen, setHistoryOpen] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get('/portfolio');
      if (data) {
        setSummary(data.summary || null);
        if (Array.isArray(data.positions)) setPositions(data.positions);
      }
    } catch {}
    try {
      const { data: m } = await api.get('/portfolio/metrics');
      if (m) setMetrics(m);
    } catch {}
    try {
      const { data: h } = await api.get('/portfolio/health');
      if (h && Array.isArray(h.positions)) {
        setHealth((prev) => {
          const next = { ...prev };
          h.positions.forEach((p) => { if (p?.id) next[p.id] = p; });
          return next;
        });
      }
    } catch {}
    setLast(new Date().toISOString());
  };
  useEffect(() => { load(); }, []);
  // Open positions refresh every 20s (visibility-aware)
  useVisibilityAwareInterval(load, 20000, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/portfolio/positions', {
        symbol: form.symbol.toUpperCase(),
        qty: parseFloat(form.qty),
        entry: parseFloat(form.entry),
        stop: form.stop ? parseFloat(form.stop) : null,
        target: form.target ? parseFloat(form.target) : null,
        note: form.note,
      });
      toast.success(`Position opened: ${(data?.symbol || form.symbol).toUpperCase()}`);
      setShowAdd(false);
      setForm({ symbol: '', qty: '', entry: '', stop: '', target: '', note: '' });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && d.violations) {
        toast.error('Discipline blocked entry', { description: d.violations.join(' · ') });
      } else if (typeof d === 'string') {
        toast.error(d);
      } else if (err?.message) {
        toast.error(`Failed to open position: ${err.message}`);
      } else {
        toast.error('Failed to open position');
      }
    }
  };

  const close = (p) => {
    // Phase 51.M+ — open the SaveModeModal instead of window.prompt.
    // Operator picks Learning / Unfinished / Discard AND supplies the
    // exit price in the same flow. Health object may carry a "current"
    // price we can prefill for convenience.
    const h = health[p.id];
    const defaultExit = h?.current ?? p.entry ?? '';
    setModal({ open: true, position: p, mode: 'close', defaultExit });
  };

  const removePos = (p) => {
    // Phase 51.M+ — same 3-way save-mode contract, but exit price is
    // sourced server-side from the last live quote (or entry fallback).
    setModal({ open: true, position: p, mode: 'archive', defaultExit: '' });
  };

  const submitModal = async ({ exit, save_mode }) => {
    const p = modal.position;
    if (!p) return;
    const isClose = modal.mode === 'close';
    try {
      if (isClose) {
        const { data } = await api.post(`/portfolio/positions/${p.id}/close`, {
          exit: parseFloat(exit),
          save_mode,
        });
        const label = {
          learning:   'closed → learning',
          unfinished: 'closed → unfinished (not in learning/truth)',
          discard:    'closed → discarded (no metrics impact)',
        }[save_mode] || 'closed';
        toast.success(`${p.symbol} ${label}`, {
          description: data?.pnl != null ? `P&L $${data.pnl.toFixed?.(2) ?? data.pnl}` : undefined,
        });
      } else {
        const { data } = await api.post(`/portfolio/positions/${p.id}/archive`, { save_mode });
        const label = {
          learning:   'archived → learning',
          unfinished: 'archived → unfinished',
          discard:    'archived → discarded',
        }[save_mode] || 'archived';
        toast.success(`${p.symbol} ${label}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isClose ? 'Close failed' : 'Archive failed'));
      throw e;
    }
  };

  // Iter 84 — begin editing a position (qty/entry/stop/target).
  const beginEdit = (p) => {
    setEditing({
      id:     p.id,
      qty:    p.qty ?? '',
      entry:  p.entry ?? '',
      stop:   p.stop ?? '',
      target: p.target ?? '',
    });
  };
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing?.id) return;
    setSaving(true);
    // Convert blank strings to null (clears the level); parse numbers.
    const parse = (v) => v === '' || v === null || v === undefined ? null : parseFloat(v);
    const payload = {
      qty:    parse(editing.qty),
      entry:  parse(editing.entry),
      stop:   parse(editing.stop),
      target: parse(editing.target),
    };
    if (payload.qty == null || !(payload.qty > 0)) { toast.error('Qty must be > 0'); setSaving(false); return; }
    if (payload.entry == null || !(payload.entry > 0)) { toast.error('Entry must be > 0'); setSaving(false); return; }
    try {
      const { data } = await api.patch(`/portfolio/positions/${editing.id}`, payload);
      toast.success('Position updated');
      // Iter 84 — optimistic merge: the PATCH response returns the fresh
      // position doc; splice it into local state immediately so the row
      // reflects the new values without waiting ~5-7s for the next
      // /api/portfolio GET (regime enrichment is slow).
      if (data && data.id) {
        setPositions((prev) => prev.map((p) => p.id === data.id ? { ...p, ...data } : p));
      }
      setEditing(null);
      load();  // still refresh in background so regime/health re-sync
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && d.violations) {
        toast.error('Discipline blocked edit', { description: d.violations.join(' · ') });
      } else if (typeof d === 'string') {
        toast.error(d);
      } else {
        toast.error('Edit failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const Stat = ({ label, value, color = '', testId }) => (
    <div className="panel p-3" data-testid={testId}>
      <div className="text-[10px] tracking-[0.3em] text-cyan-sys uppercase font-display">{label}</div>
      <div className={`font-mono text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );

  const pnlCol = (v) => v > 0 ? 'text-buy' : v < 0 ? 'text-sell' : '';

  // Auto-Exit watchdog — every time `health` or `positions` refresh
  // AND the toggle is ON, scan for any open position whose last price
  // has crossed its stop or target. Fire the existing close endpoint
  // with save_mode='learning' so the learning ledger absorbs both
  // wins and stopouts automatically. Idempotency: we key by position
  // id in a ref set so a position isn't closed twice.
  const closingRef = React.useRef(new Set());
  useEffect(() => {
    if (!autoExit || !positions?.length) return;
    for (const p of positions) {
      if (!p?.id || p.exit != null || closingRef.current.has(p.id)) continue;
      const h = health[p.id];
      const last = h?.current;
      if (last == null) continue;
      const stop = p.stop == null ? null : parseFloat(p.stop);
      const tgt = p.target == null ? null : parseFloat(p.target);
      let hit = null;
      // Long convention: stop < entry < target.
      if (tgt != null && last >= tgt) hit = 'target';
      else if (stop != null && last <= stop) hit = 'stop';
      if (!hit) continue;
      closingRef.current.add(p.id);
      (async () => {
        try {
          await api.post(`/portfolio/positions/${p.id}/close`, {
            exit: last,
            save_mode: 'learning',
          });
          toast.success(`${p.symbol} auto-exited @ ${last} (${hit} hit → learning)`);
          load();
        } catch { /* leave in flight so a manual close still works */
          closingRef.current.delete(p.id);
        }
      })();
    }
  }, [autoExit, positions, health]);

  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total P&L" value={`$${summary?.total_pnl ?? 0}`} color={pnlCol(summary?.total_pnl || 0)} />
        <Stat label="Unrealized" value={`$${summary?.unrealized_pnl ?? 0}`} color={pnlCol(summary?.unrealized_pnl || 0)} />
        {/* Phase 36.5.C — Win-rate derives from canonical metrics; N/A when no closed trades. */}
        <Stat
          label="Win Rate"
          testId="portfolio-stat-winrate"
          value={
            metrics
              ? (metrics.win_rate == null ? 'N/A' : `${metrics.win_rate}%`)
              : (summary?.win_rate != null && summary?.closed_count > 0 ? `${summary.win_rate}%` : 'N/A')
          }
          color="text-cyan-sys"
        />
        <Stat label="Exposure" value={`$${summary?.exposure ?? 0}`} color="text-hold" />
      </div>

      {/* Iter 110 — Recent Openings + Recent Closes. Two separate
          stacked feeds surfacing the ~10 newest open & close events so
          the operator can see what fired recently without scrolling the
          whole positions table. */}
      <RecentOpenings positions={positions} />
      <RecentCloses    positions={positions} />

      <CornerPanel
        title={`Positions // ${summary?.open_count ?? 0} open / ${summary?.closed_count ?? 0} closed`}
        right={
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <RefreshButton onRefresh={load} lastUpdated={last} testId="portfolio-refresh" />
            <button
              data-testid="portfolio-auto-exit-toggle"
              onClick={toggleAutoExit}
              title={autoExit
                ? 'Auto-Exit ON — active entries close automatically to learning when price hits stop or target'
                : 'Auto-Exit OFF — hits still fire but do not close positions'}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${autoExit
                ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
                : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
            >
              auto-exit {autoExit ? 'on' : 'off'}
            </button>
            <button
              data-testid="portfolio-add-btn"
              onClick={() => setShowAdd((v) => !v)}
              className="flex items-center gap-1 text-[11px] font-display tracking-[0.25em] uppercase border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-3 py-1"
            >
              <Plus size={12} /> new position
            </button>
          </div>
        }
        testId="portfolio-panel"
      >
        {showAdd && (
          <form onSubmit={add} className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 p-3 border border-[var(--border-muted)] bg-black" data-testid="portfolio-add-form">
            {['symbol','qty','entry','stop','target'].map((k) => (
              <input
                key={k}
                required={['symbol','qty','entry'].includes(k)}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={k.toUpperCase()}
                className="bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-2 py-1 font-mono text-[12px]"
                data-testid={`portfolio-field-${k}`}
              />
            ))}
            <button
              type="submit"
              data-testid="portfolio-submit"
              className="border border-buy text-buy hover:bg-buy hover:text-black font-display tracking-[0.25em] uppercase text-[11px]"
            >
              open
            </button>
          </form>
        )}

        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[900px]">
            <thead>
              <tr className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys border-b border-[var(--border-default)]">
                <th className="text-left py-2 px-2">symbol</th>
                <th className="text-right py-2 px-2">qty</th>
                <th className="text-right py-2 px-2">entry</th>
                <th className="text-right py-2 px-2">stop</th>
                <th className="text-right py-2 px-2">target</th>
                <th className="text-right py-2 px-2">exit</th>
                <th className="text-right py-2 px-2">pnl</th>
                <th className="text-center py-2 px-2">health</th>
                <th className="text-center py-2 px-2">exit risk</th>
                <th className="text-left py-2 px-2">status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && (
                <tr><td colSpan={11} className="text-center py-6 text-[var(--txt-muted)]">// no positions</td></tr>
              )}
              {positions.map((p) => {
                const h = health[p.id];
                const score = h?.score;
                const triggers = h?.exit_triggers || [];
                const scoreColor = score == null ? '' : score >= 70 ? 'text-buy' : score >= 40 ? 'text-hold' : 'text-sell';
                const er = p.exit_risk;
                const erColor = !er ? '' :
                  er.status === 'EXIT_EARLY'     ? 'text-sell' :
                  er.status === 'HOLD_CAUTIOUS'  ? 'text-hold' :
                  'text-buy';
                const erBadge = !er ? null :
                  er.status === 'EXIT_EARLY'    ? 'EXIT'  :
                  er.status === 'HOLD_CAUTIOUS' ? 'CAUTION' :
                  'STRONG';
                const erTooltip = (er?.reasons || []).join(' · ') || 'no degradation detected';
                const isEditing = editing && editing.id === p.id;
                // Iter 84 — regime + company name for display under ticker
                const reg = p.regime || {};
                const regimeStr = (reg.trend || reg.volatility)
                  ? `${reg.trend || '—'}${reg.volatility ? ' / ' + reg.volatility : ''}`
                  : null;
                const cname = p.company_name && p.company_name !== p.symbol ? p.company_name : null;
                const editableInput = (field, extraClass = '') => (
                  <input
                    data-testid={`position-edit-${field}-${p.symbol}`}
                    type="number"
                    step="any"
                    value={editing[field] ?? ''}
                    onChange={(e) => setEditing({ ...editing, [field]: e.target.value })}
                    className={`w-full bg-black border border-cyan-sys text-cyan-sys outline-none px-1 py-0.5 font-mono text-[11px] text-right ${extraClass}`}
                  />
                );
                // Iter 85 — inline discipline preview. Long positions
                // require stop < entry and target > entry; short is the
                // mirror. This is a client-side warning only — the
                // backend still enforces the firewall on save. Shown as
                // a small red hint just below the row when editing.
                const disciplineWarnings = [];
                if (isEditing) {
                  const e = parseFloat(editing.entry);
                  const s = parseFloat(editing.stop);
                  const t = parseFloat(editing.target);
                  const isLong = !(p.direction === 'SHORT');
                  if (Number.isFinite(e) && Number.isFinite(s)) {
                    if (isLong && s >= e) disciplineWarnings.push('stop must be < entry for LONG');
                    if (!isLong && s <= e) disciplineWarnings.push('stop must be > entry for SHORT');
                  }
                  if (Number.isFinite(e) && Number.isFinite(t)) {
                    if (isLong && t <= e) disciplineWarnings.push('target must be > entry for LONG');
                    if (!isLong && t >= e) disciplineWarnings.push('target must be < entry for SHORT');
                  }
                  if (Number.isFinite(e) && Number.isFinite(s) && Number.isFinite(t)) {
                    const risk = Math.abs(e - s);
                    const reward = Math.abs(t - e);
                    if (risk > 0 && reward / risk < 1) {
                      disciplineWarnings.push(`R:R only ${(reward/risk).toFixed(2)} (need ≥ 1)`);
                    }
                  }
                }
                // Iter 85 — position edit history (audit trail).
                const edits = Array.isArray(p.edits) ? p.edits : [];
                const hasEdits = edits.length > 0;
                const isHistoryOpen = !!historyOpen[p.id];
                return (
                <React.Fragment key={p.id}>
                <tr className="border-b border-[var(--border-default)]" data-testid={`position-row-${p.symbol}`}>
                  <td className="py-2 px-2 align-top">
                    <div className="font-bold flex items-center gap-1.5">
                      {p.symbol}
                      {hasEdits && (
                        <button
                          onClick={() => setHistoryOpen((h) => ({ ...h, [p.id]: !h[p.id] }))}
                          data-testid={`position-history-toggle-${p.symbol}`}
                          className="text-[9px] tracking-widest uppercase px-1 py-0 border border-cyan-sys/40 text-cyan-sys/70 hover:border-cyan-sys hover:text-cyan-sys transition-colors"
                          title={`${edits.length} edit${edits.length===1?'':'s'} — click to ${isHistoryOpen ? 'hide' : 'view'}`}
                        >
                          {isHistoryOpen ? '▼' : '▸'}{edits.length}
                        </button>
                      )}
                    </div>
                    {cname && (
                      <div data-testid={`position-company-${p.symbol}`}
                           className="text-[10px] text-[var(--txt-muted)] leading-tight truncate max-w-[180px]"
                           title={cname}>
                        {cname}
                      </div>
                    )}
                    {regimeStr && (
                      <div data-testid={`position-regime-${p.symbol}`}
                           className="text-[9px] text-cyan-sys/70 uppercase tracking-wider leading-tight"
                           title="Current regime — trend / volatility">
                        {regimeStr}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right">{isEditing ? editableInput('qty') : p.qty}</td>
                  <td className="py-2 px-2 text-right">{isEditing ? editableInput('entry') : p.entry}</td>
                  <td className="py-2 px-2 text-right text-sell">{isEditing ? editableInput('stop') : (p.stop ?? '—')}</td>
                  <td className="py-2 px-2 text-right text-buy">{isEditing ? editableInput('target') : (p.target ?? '—')}</td>
                  <td className="py-2 px-2 text-right">{p.exit ?? '—'}</td>
                  <td className={`py-2 px-2 text-right ${pnlCol(p.pnl || 0)}`}>{p.pnl ?? (h?.live_pnl ?? '—')}</td>
                  <td className={`py-2 px-2 text-center font-bold ${scoreColor}`} data-testid={`position-health-${p.symbol}`}>
                    {p.status === 'open' && score != null ? score : '—'}
                  </td>
                  <td className="py-2 px-2 text-center" data-testid={`position-exit-risk-${p.symbol}`} title={erTooltip}>
                    {p.status === 'open' && er ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-bold ${erColor}`}>{er.exit_risk_score}</span>
                        <span className={`text-[9px] uppercase tracking-wider px-1 border ${erColor} border-current`}>
                          {erBadge}
                        </span>
                      </div>
                    ) : <span className="text-[var(--txt-muted)]">—</span>}
                  </td>
                  <td className={`py-2 px-2 ${p.status === 'open' ? 'text-cyan-sys' : 'text-[var(--txt-secondary)]'}`}>
                    {p.status}
                    {triggers.length > 0 && (
                      <div className="text-[10px] text-sell uppercase tracking-wider">▸ {triggers[0].replace(/_/g, ' ')}</div>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={saveEdit}
                            disabled={saving}
                            className="text-buy hover:underline text-[11px] inline-flex items-center gap-0.5 disabled:opacity-50"
                            data-testid={`position-save-${p.symbol}`}
                            title="save changes"
                          >
                            <Check size={12} /> save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-[var(--txt-secondary)] hover:text-sell hover:underline text-[11px]"
                            data-testid={`position-cancel-${p.symbol}`}
                            title="cancel"
                          >
                            cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {p.status === 'open' && (
                            <>
                              <button
                                onClick={() => beginEdit(p)}
                                className="text-cyan-sys hover:underline text-[11px] inline-flex items-center gap-0.5"
                                data-testid={`position-edit-${p.symbol}`}
                                title="edit qty / entry / stop / target"
                              >
                                <Pencil size={11} /> edit
                              </button>
                              <button onClick={() => close(p)} className="text-sell hover:underline text-[11px]" data-testid={`position-close-${p.symbol}`}>close</button>
                            </>
                          )}
                          <button
                            onClick={() => removePos(p)}
                            className="text-[var(--txt-secondary)] hover:text-sell hover:underline text-[11px]"
                            title="Archive this position (hide from the active list; full history preserved)"
                            data-testid={`position-remove-${p.symbol}`}
                          >
                            remove
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {isEditing && disciplineWarnings.length > 0 && (
                  <tr data-testid={`position-warnings-${p.symbol}`} className="border-b border-[var(--border-default)]">
                    <td colSpan={11} className="py-1 px-2 text-[10px] text-hold bg-[#171207]">
                      <div className="flex items-center gap-2">
                        <span className="uppercase tracking-widest text-hold/80 text-[9px]">▲ warnings</span>
                        <span className="text-hold">{disciplineWarnings.join(' · ')}</span>
                        <span className="text-[9px] text-[var(--txt-muted)]">(save will still be attempted; the firewall may block)</span>
                      </div>
                    </td>
                  </tr>
                )}
                {isHistoryOpen && hasEdits && (
                  <tr data-testid={`position-history-${p.symbol}`} className="border-b border-[var(--border-default)]">
                    <td colSpan={11} className="py-2 px-2 bg-[#0a0d0f]">
                      <div className="text-[9px] uppercase tracking-widest text-cyan-sys/70 mb-1.5">Edit History · {edits.length}</div>
                      <div className="space-y-1">
                        {edits.slice().reverse().map((ev, i) => {
                          const changes = Object.keys(ev.after || {}).map((k) => {
                            const b = ev.before?.[k];
                            const a = ev.after?.[k];
                            return `${k}: ${b ?? '—'} → ${a ?? '—'}`;
                          }).join(' · ');
                          const when = (() => { try { return new Date(ev.at).toLocaleString(); } catch { return ev.at; } })();
                          return (
                            <div key={i} className="text-[10px] font-mono text-[var(--txt-secondary)] flex items-baseline gap-2">
                              <span className="text-cyan-sys/60 shrink-0">{when}</span>
                              <span className="text-[var(--txt-muted)] shrink-0">·</span>
                              <span className="text-[var(--txt-secondary)] shrink-0">{ev.by || 'unknown'}</span>
                              <span className="text-[var(--txt-muted)] shrink-0">·</span>
                              <span className="text-hold truncate">{changes || '(no field changes)'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );})}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      <SaveModeModal
        open={modal.open}
        onClose={() => setModal({ open: false, position: null, mode: 'close' })}
        symbol={modal.position?.symbol || ''}
        defaultExit={modal.defaultExit ?? ''}
        requireExit={modal.mode === 'close'}
        mode={modal.mode}
        onSubmit={submitModal}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Iter 110 — Recent Openings + Recent Closes feeds.
//
// Two compact, independent feeds that answer "what happened recently?"
// at a glance so the operator doesn't have to scroll the main positions
// table. Each list caps at 10 entries, newest first. Pulled from the
// already-loaded `positions` array so this costs 0 extra API calls.
// ─────────────────────────────────────────────────────────────────────

const SAVE_MODE_LABEL = {
  learning:   { txt: 'learning',   cls: 'text-cyan-sys' },
  unfinished: { txt: 'unfinished', cls: 'text-hold' },
  discard:    { txt: 'discarded',  cls: 'text-[var(--txt-muted)]' },
};

function _fmtPnl(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}`;
}

function _fmtTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    });
  } catch { return '—'; }
}

function RecentOpenings({ positions }) {
  const openings = React.useMemo(() => (
    (positions || [])
      .slice()
      .sort((a, b) => String(b.opened_at || '').localeCompare(String(a.opened_at || '')))
      .slice(0, 10)
  ), [positions]);

  return (
    <CornerPanel
      title={`Recent Openings // ${openings.length}`}
      testId="portfolio-recent-openings"
    >
      {openings.length === 0 ? (
        <div className="text-[11px] font-mono text-[var(--txt-muted)] py-2 px-1">
          // no positions opened yet.
        </div>
      ) : (
        <div className="divide-y divide-[var(--border-default)]">
          {openings.map((p) => (
            <div
              key={`open-${p.id}`}
              data-testid={`recent-opening-${p.symbol}`}
              className="flex items-center justify-between gap-3 py-2 px-1 text-[11px] font-mono"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-cyan-sys shrink-0">▸</span>
                <span className="text-cyan-sys font-bold tracking-wider shrink-0 min-w-[64px]">
                  {p.symbol}
                </span>
                <span className="text-[var(--txt-secondary)] shrink-0">
                  qty <span className="text-[var(--txt-primary)]">{p.qty}</span>
                </span>
                <span className="text-[var(--txt-secondary)] shrink-0">
                  @ <span className="text-[var(--txt-primary)]">${Number(p.entry || 0).toFixed(2)}</span>
                </span>
                {p.stop != null && (
                  <span className="text-sell/80 shrink-0 hidden sm:inline">
                    stop ${Number(p.stop).toFixed(2)}
                  </span>
                )}
                {p.target != null && (
                  <span className="text-buy/80 shrink-0 hidden sm:inline">
                    tgt ${Number(p.target).toFixed(2)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-[10px] uppercase tracking-widest ${
                  p.status === 'open' ? 'text-buy' : 'text-[var(--txt-muted)]'
                }`}>
                  {p.status === 'open' ? '● open' : '○ ' + (p.status || '—')}
                </span>
                <span className="text-[var(--txt-muted)]">{_fmtTs(p.opened_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </CornerPanel>
  );
}

function RecentCloses({ positions }) {
  const closes = React.useMemo(() => (
    (positions || [])
      .filter((p) => p.status === 'closed' && p.closed_at)
      .slice()
      .sort((a, b) => String(b.closed_at || '').localeCompare(String(a.closed_at || '')))
      .slice(0, 10)
  ), [positions]);

  return (
    <CornerPanel
      title={`Recent Closes // ${closes.length}`}
      testId="portfolio-recent-closes"
    >
      {closes.length === 0 ? (
        <div className="text-[11px] font-mono text-[var(--txt-muted)] py-2 px-1">
          // no closed positions yet.
        </div>
      ) : (
        <div className="divide-y divide-[var(--border-default)]">
          {closes.map((p) => {
            const mode = p.close_reason === 'archived_at_market' || p.close_reason === 'archived_at_entry_fallback'
              ? 'learning'
              : (p.close_reason === 'unfinished_data' ? 'unfinished'
              :  p.close_reason === 'discarded_incomplete' ? 'discard'
              :  p.save_mode || 'learning');
            const label = SAVE_MODE_LABEL[mode] || SAVE_MODE_LABEL.learning;
            const pnl = p.pnl;
            const win = pnl != null && Number(pnl) > 0;
            return (
              <div
                key={`close-${p.id}`}
                data-testid={`recent-close-${p.symbol}`}
                className="flex items-center justify-between gap-3 py-2 px-1 text-[11px] font-mono"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={win ? 'text-buy shrink-0' : 'text-sell shrink-0'}>
                    {win ? '▲' : '▼'}
                  </span>
                  <span className="text-cyan-sys font-bold tracking-wider shrink-0 min-w-[64px]">
                    {p.symbol}
                  </span>
                  <span className="text-[var(--txt-secondary)] shrink-0 hidden sm:inline">
                    ${Number(p.entry || 0).toFixed(2)} → ${Number(p.exit || 0).toFixed(2)}
                  </span>
                  <span className={`shrink-0 ${win ? 'text-buy' : 'text-sell'}`}>
                    {_fmtPnl(pnl)}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[9px] uppercase tracking-widest ${label.cls}`}>
                    {label.txt}
                  </span>
                  <span className="text-[var(--txt-muted)]">{_fmtTs(p.closed_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CornerPanel>
  );
}
